﻿import {isNullOrEmpty, StringNamedValue, translation} from "@2d-front-monorepo/core";
import {
	useInitializeMvvmContext,
	MvvmDataProvider,
	MvvmDataProviderRef,
	MvvmSelect
} from "@2d-front-monorepo/design-library";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, RefObject, useCallback, useMemo} from "react";
import VehiclePropertyFlags from "../enums/VehiclePropertyFlags";
import useCreateSelectItems from "../helpers/useCreateSelectItems";
import {useScrollToFirstError} from "../helpers/scrollToFirstError";
import selectOrCreate from "../helpers/selectOrCreate";
import useEntityAttachments from "../helpers/useEntityAttachments";
import {VehicleId} from "../nominalTypes/VehicleId";
import {EntityProfile} from "../types/InstallationByHostData";
import {VehicleResult} from "../types/VehicleResult";
import {ComponentParts, ComponentWithChildrenParts} from "./ComponentParts";
import CreateVehicleComponent, {CreateVehicleModel, PlateValidationRequirements} from "./CreateVehicleComponent";
import FullCreateAttachmentsComponent from "./FullCreateAttachmentsComponent";
import PermitsMvvmErrorCollector from "./PermitsMvvmErrorCollector";
import VehicleComponent from "./VehicleComponent";

export interface CreateOrSelectVehicleComponentProps extends ComponentParts<CreateOrSelectVehicleModel> {
	colors: StringNamedValue[];
	configuration: EntityProfile<VehiclePropertyFlags>;
	contextRef?: RefObject<MvvmDataProviderRef<CreateOrSelectVehicleModel>>;
	createEnabled?: boolean;
	createParts?: ComponentWithChildrenParts<CreateVehicleModel>;
	filesPrefix: string;
	initialState: CreateOrSelectVehicleModel;
	models: StringNamedValue[];
	onSubmit: (formData: CreateOrSelectVehicleModel) => Promise<void>;
	plateValidation: PlateValidationRequirements;
	defaultProvince?: string | undefined;
	provinces: StringNamedValue[];
	selectParts?: ComponentWithChildrenParts<CreateOrSelectVehicleModel>;
	vehicles?: VehicleResult[];
}

export interface CreateOrSelectVehicleModel extends CreateVehicleModel {
	vehicleId: VehicleId;
}

function getVehicleLabel(vehicle: VehicleResult): string {
	if (isNullOrEmpty(vehicle.data?.nickname)) {
		return vehicle.plate;
	}

	return `${vehicle.plate} - ${vehicle.data!.nickname}`;
}

export default function CreateOrSelectVehicleComponent(props: CreateOrSelectVehicleComponentProps): JSX.Element {
	const {
		initialState,
		onSubmit,
		filesPrefix,
		vehicles,
		contextRef,
		createEnabled,
		colors,
		models,
		provinces,
		header,
		footer,
		plateValidation,
		configuration,
		createParts,
		selectParts
	} = props;

	const choose = useCreateSelectItems(vehicles, createEnabled, getVehicleLabel);
	const multiple = choose.length > 1;

	const {context} = useInitializeMvvmContext({
		initialState: () => {
			initialState.vehicleId = selectOrCreate(choose, initialState.vehicleId);
			const province = (initialState.arguments.data?.province ?? props.defaultProvince)?.toUpperCase();
			initialState.arguments.data = initialState.arguments.data ?? {};
			initialState.arguments.data.province = province;
			return initialState;
		},
		contextRef: contextRef
	}, [initialState]);

	const state = context.state;
	const selectedVehicle = useMemo(() => vehicles?.find(r => r.id === state.vehicleId), [vehicles, state.vehicleId]);
	const attachments = useEntityAttachments(selectedVehicle, configuration.attachments);
	const createVisible = createEnabled && state.vehicleId === "create";
	const hiddenFields = configuration.fieldRules.hidden || VehiclePropertyFlags.None;
	const formRef = useScrollToFirstError(context);

	// ponytail: a plate with no match here makes the button a no-op. Can't happen today - every
	// caller derives plateValidation.existing from this same `vehicles` list (or a subset of it).
	const selectExistingByPlate = useCallback(function (plate: string): void {
		const match = vehicles?.find(v => v.plate.toUpperCase() === plate.toUpperCase());
		if (match != null) context.setValue("vehicleId", match.id);
	}, [vehicles, context]);

	const plateValidationWithSelect = useMemo(
		() => ({...plateValidation, onSelectExisting: selectExistingByPlate}),
		[plateValidation, selectExistingByPlate]);

	return <MvvmDataProvider<CreateOrSelectVehicleModel> onSubmit={onSubmit} context={context} ref={formRef}>
		<PermitsMvvmErrorCollector/>
		{header?.(context.state)}

		<div className="layout vertical gap">
		{multiple && <MvvmSelect context={context}
                                 label={translation(PermitTranslations.new_vehicle__choose)}
                                 property="vehicleId"
                                 items={choose}/>}

		{selectedVehicle && <>
			{selectParts?.header?.(context.state)}
            <VehicleComponent vehicle={selectedVehicle} filesPrefix={filesPrefix} hiddenEntityProperties={hiddenFields}/>
			{selectParts?.children}
            <FullCreateAttachmentsComponent context={context} attachments={attachments}/>
			{selectParts?.footer?.(context.state)}
        </>}

		{createVisible &&
            <CreateVehicleComponent context={context}
                                    configuration={configuration}
                                    colors={colors}
                                    models={models}
                                    provinces={provinces}
                                    plateValidation={plateValidationWithSelect}
                                    header={createParts?.header}
                                    children={createParts?.children}
                                    footer={createParts?.footer}/>
		}
		</div>
		{footer?.(context.state)}
	</MvvmDataProvider>;
}
